import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface PlateauedExercise {
  exerciseId: string;
  exerciseName: string;
  currentWeight: number;
  weeksSinceProgress: number;
  lastProgressDate: Date | null;
  suggestedAction: string;
  recentWeights: { week: string; maxWeight: number }[];
}

export interface PlateauDetectionResult {
  plateauedExercises: PlateauedExercise[];
  hasPlateaus: boolean;
  totalExercisesAnalyzed: number;
}

interface SetLogWithDetails {
  weight: number | null;
  created_at: string | null;
  exercise_id: string;
  exercises: { name: string } | null;
  workout_logs: { started_at: string } | null;
}

const PLATEAU_THRESHOLD_WEEKS = 3; // Consider it a plateau if no progress in 3+ weeks
const ANALYSIS_WEEKS = 8; // Look back 8 weeks for analysis

export function usePlateauDetection(clientId: string | undefined) {
  const [result, setResult] = useState<PlateauDetectionResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPlateaus = useCallback(async () => {
    if (!clientId) {
      setResult(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - (ANALYSIS_WEEKS * 7));

      // Get workout logs for this client
      const { data: workoutLogs } = await supabase
        .from('workout_logs')
        .select('id, started_at')
        .eq('client_id', clientId)
        .gte('started_at', startDate.toISOString())
        .order('started_at', { ascending: true });

      if (!workoutLogs || workoutLogs.length === 0) {
        setResult({
          plateauedExercises: [],
          hasPlateaus: false,
          totalExercisesAnalyzed: 0,
        });
        setLoading(false);
        return;
      }

      const workoutLogIds = workoutLogs.map(wl => wl.id);

      // Get all set logs with exercise names
      const { data: setLogs } = await supabase
        .from('set_logs')
        .select(`
          weight,
          created_at,
          exercise_id,
          workout_log_id,
          exercises:exercise_id (name)
        `)
        .in('workout_log_id', workoutLogIds)
        .not('weight', 'is', null);

      if (!setLogs || setLogs.length === 0) {
        setResult({
          plateauedExercises: [],
          hasPlateaus: false,
          totalExercisesAnalyzed: 0,
        });
        setLoading(false);
        return;
      }

      // Create a map of workout_log_id to started_at
      const workoutDateMap = new Map(
        workoutLogs.map(wl => [wl.id, wl.started_at])
      );

      // Group sets by exercise, then by week
      const exerciseWeeklyData = new Map<
        string,
        {
          name: string;
          weeklyMaxWeights: Map<string, number>;
        }
      >();

      for (const set of setLogs as (typeof setLogs[0] & { workout_log_id: string })[]) {
        if (!set.weight || !set.exercise_id) continue;

        const workoutDate = workoutDateMap.get(set.workout_log_id);
        if (!workoutDate) continue;

        const weekKey = getWeekKey(new Date(workoutDate));
        const exerciseName = (set.exercises as { name: string } | null)?.name || 'Unknown';

        if (!exerciseWeeklyData.has(set.exercise_id)) {
          exerciseWeeklyData.set(set.exercise_id, {
            name: exerciseName,
            weeklyMaxWeights: new Map(),
          });
        }

        const exerciseData = exerciseWeeklyData.get(set.exercise_id)!;
        const currentMax = exerciseData.weeklyMaxWeights.get(weekKey) || 0;
        if (set.weight > currentMax) {
          exerciseData.weeklyMaxWeights.set(weekKey, set.weight);
        }
      }

      // Analyze each exercise for plateaus
      const plateauedExercises: PlateauedExercise[] = [];

      for (const [exerciseId, data] of exerciseWeeklyData) {
        // Need at least 4 weeks of data to detect a plateau
        if (data.weeklyMaxWeights.size < 4) continue;

        const weeklyWeights = Array.from(data.weeklyMaxWeights.entries())
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([week, weight]) => ({ week, maxWeight: weight }));

        const plateau = detectPlateau(weeklyWeights);

        if (plateau.isPlateaued) {
          plateauedExercises.push({
            exerciseId,
            exerciseName: data.name,
            currentWeight: weeklyWeights[weeklyWeights.length - 1].maxWeight,
            weeksSinceProgress: plateau.weeksSinceProgress,
            lastProgressDate: plateau.lastProgressDate,
            suggestedAction: generateSuggestion(plateau.weeksSinceProgress, data.name),
            recentWeights: weeklyWeights.slice(-6),
          });
        }
      }

      // Sort by weeks since progress (most stalled first)
      plateauedExercises.sort((a, b) => b.weeksSinceProgress - a.weeksSinceProgress);

      setResult({
        plateauedExercises,
        hasPlateaus: plateauedExercises.length > 0,
        totalExercisesAnalyzed: exerciseWeeklyData.size,
      });
    } catch (err) {
      console.error('Error detecting plateaus:', err);
      setError('Failed to analyze exercise progression');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadPlateaus();
  }, [loadPlateaus]);

  return { result, loading, error, reload: loadPlateaus };
}

function getWeekKey(date: Date): string {
  // Get ISO week number and year
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${d.getFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

interface PlateauAnalysis {
  isPlateaued: boolean;
  weeksSinceProgress: number;
  lastProgressDate: Date | null;
}

function detectPlateau(
  weeklyWeights: { week: string; maxWeight: number }[]
): PlateauAnalysis {
  if (weeklyWeights.length < 4) {
    return { isPlateaued: false, weeksSinceProgress: 0, lastProgressDate: null };
  }

  // Find the last time weight increased
  let lastProgressWeekIndex = -1;
  let previousMax = 0;

  for (let i = 0; i < weeklyWeights.length; i++) {
    if (weeklyWeights[i].maxWeight > previousMax) {
      lastProgressWeekIndex = i;
      previousMax = weeklyWeights[i].maxWeight;
    }
  }

  // Calculate weeks since progress
  const weeksSinceProgress = weeklyWeights.length - 1 - lastProgressWeekIndex;

  // Parse the week string to get a date
  let lastProgressDate: Date | null = null;
  if (lastProgressWeekIndex >= 0) {
    const weekStr = weeklyWeights[lastProgressWeekIndex].week;
    const [year, weekPart] = weekStr.split('-W');
    const weekNum = parseInt(weekPart);
    lastProgressDate = getDateFromWeek(parseInt(year), weekNum);
  }

  return {
    isPlateaued: weeksSinceProgress >= PLATEAU_THRESHOLD_WEEKS,
    weeksSinceProgress,
    lastProgressDate,
  };
}

function getDateFromWeek(year: number, week: number): Date {
  const jan1 = new Date(year, 0, 1);
  const days = (week - 1) * 7;
  const result = new Date(jan1);
  result.setDate(jan1.getDate() + days);
  return result;
}

function generateSuggestion(weeksSinceProgress: number, exerciseName: string): string {
  if (weeksSinceProgress >= 6) {
    return `Consider a deload week or trying a different ${exerciseName} variation to break through`;
  } else if (weeksSinceProgress >= 4) {
    return `Try adding drop sets or increasing volume to stimulate new growth`;
  } else {
    return `Consider micro-loading (1-2.5lb increments) or adding 1-2 extra reps before increasing weight`;
  }
}
